import { IEventSystem } from "@dhx/ts-common/events";
import { DataCollection, DataEvents, isTreeCollection } from "@dhx/ts-data";

import {
	ChartEvents,
	HorizontalPosition,
	ILegendConfig,
	ILegendDrawData,
	SvgElement,
	VerticalPosition,
} from "./types";
import { IFitPosition } from "@dhx/ts-common/html";

import { sv } from "@dhx/ts-common/dom";
import { IHandlers } from "@dhx/ts-common/types";
import { getDefaultColor, getFontStyle, getTextWidth, locator, verticalCenteredText } from "./helpers/common";
import { legendShape } from "./shapes/legend";

function getDefaultMargin(halign: HorizontalPosition, valign: VerticalPosition, isTreeMap: boolean) {
	switch (valign) {
		case "middle": {
			switch (halign) {
				case "right":
					return isTreeMap ? 20 : 60;
				case "left":
					return 120;
				case "center":
					throw new Error("cant place legend on center, middle");
			}
		}
		// eslint-disable-next-line no-fallthrough
		case "top":
			return 20;
		case "bottom":
			return isTreeMap ? 20 : 60;
	}
}

export class Legend {
	private config: ILegendConfig;
	private _handlers: IHandlers;

	constructor(
		private _data: DataCollection<any>,
		config: ILegendConfig,
		private _events: IEventSystem<DataEvents | ChartEvents>
	) {
		const defaults = {
			form: "rect",
			itemPadding: 20,
			halign: "right",
			valign: "top",
			direction: "row",
			type: "groupName",
		};
		this.config = { ...defaults, ...config } as ILegendConfig;
		this.config.margin =
			config.margin ||
			getDefaultMargin(this.config.halign, this.config.valign, isTreeCollection(this._data));
		this._handlers = {
			onclick: (id: string, pieLike: object | undefined) =>
				this._events.fire(ChartEvents.toggleSeries, [id, pieLike]),
			onkeyup: (id: string, pieLike: boolean, event) => {
				// FIXME: set handler correctly according app code style
				event.preventDefault();
				// enter or space
				if (event.key === "Enter" || event.key === " ") {
					this._events.fire(ChartEvents.toggleSeries, [id, pieLike]);
				}
			},
		};
	}

	scaleReady(sizes: IFitPosition): void {
		const isColumnInCorner =
			this.config.direction === "column" &&
			(this.config.halign === "left" || this.config.halign === "right");
		switch (this.config.valign) {
			case "middle":
				if (this.config.halign === "right") {
					sizes.right += this.config.size >= 0 ? this.config.size : 200;
				} else if (this.config.halign === "left") {
					sizes.left += this.config.size >= 0 ? this.config.size : 200;
				}
				break;
			case "top":
				if (isColumnInCorner) {
					sizes[this.config.halign] += this.config.size >= 0 ? this.config.size : 200;
				} else {
					sizes.top += this.config.size >= 0 ? this.config.size : 40;
				}
				break;
			case "bottom":
				if (isColumnInCorner) {
					sizes[this.config.halign] += this.config.size >= 0 ? this.config.size : 200;
				} else {
					sizes.bottom += this.config.size >= 0 ? this.config.size : 40;
				}
				break;
		}
	}

	paint(width: number, height: number): SvgElement {
		const getLegendAriaAttrs = item => ({
			role: "button",
			"aria-label": item.active ? `Hide chart ${item.text}` : `Show chart ${item.text}`,
		});
		const data = this._getData();
		const config = this.config;
		config.$sizes = {
			width: 0,
			height: 0,
		};

		let positionX;
		let positionY;
		const font = getFontStyle("legend-text");
		const figureWidth = 10; // get Figure width from config ??
		const { margin, itemPadding } = config;
		const svg = [];
		const isMid = config.valign === "middle";
		let xPadding = 0;
		let yPadding = 0;
		let xPaddingMax = 0;
		let yPaddingMax = 0;
		data.forEach((item, index) => {
			if (!isMid && config.direction === "row") {
				const textWidth = getTextWidth(item.text, font);
				if (xPadding + textWidth + figureWidth * 1.5 > width && index !== 0) {
					xPadding = 0;
					yPadding += itemPadding + 2;
				}
			}
			svg.push(
				sv(
					"g",
					{
						transform: `translate(${xPadding},${yPadding})`,
						onclick: [this._handlers.onclick, item.id, this.config.values],
						onkeyup: [this._handlers.onkeyup, item.id, this.config.values],
						class: `legend-item ${!item.active ? "not-active" : ""}`,
						...getLegendAriaAttrs(item),
						tabindex: 0,
					},
					[
						sv(
							"text",
							{
								x: figureWidth / 2 + 5,
								y: 0,
								class: "start-text legend-text",
							},
							[verticalCenteredText(item.text)]
						),
						legendShape(config.form, item),
					]
				)
			);

			const itemWidth = figureWidth * 1.5 + getTextWidth(item.text, font);
			if (!isMid && config.direction === "row") {
				xPadding += itemWidth + itemPadding;
				xPaddingMax = xPaddingMax > xPadding ? xPaddingMax : xPadding;
			} else {
				xPadding = 0;
				xPaddingMax = xPaddingMax > itemWidth ? xPaddingMax : itemWidth;
				yPadding += itemPadding + 2;
				yPaddingMax = yPaddingMax > yPadding ? yPaddingMax : yPadding;
			}
		});

		switch (config.valign) {
			case "top":
				if (config.direction === "row") {
					positionY = -margin - yPadding - figureWidth / 2;
				} else {
					positionY =
						config.halign === "center"
							? -yPaddingMax + figureWidth / 2
							: margin + figureWidth / 2;
				}
				break;
			case "middle":
				positionY = (height - yPaddingMax) / 2 + itemPadding / 2;
				break;
			case "bottom":
				if (config.direction === "row") {
					positionY = height + margin;
				} else {
					positionY =
						height + figureWidth - (config.halign === "center" ? 0 : margin + yPaddingMax);
				}
				break;
		}
		switch (config.halign) {
			case "left":
				positionX = isMid
					? -xPaddingMax
					: figureWidth / 2 - (config.direction === "row" ? 0 : config.size || 200);
				break;
			case "center":
				positionX = (width - xPaddingMax) / 2;
				break;
			case "right":
				if (isMid) {
					positionX = width + margin + figureWidth / 2;
				} else if (config.direction === "row") {
					positionX = width - xPaddingMax + itemPadding + figureWidth / 2;
				} else {
					positionX = width + margin + figureWidth / 2;
				}
				break;
		}

		return sv(
			"g",
			{
				transform: `translate(${positionX}, ${positionY})`,
				"aria-label": "Legend",
				tabindex: 0,
			},
			svg
		);
	}

	protected _getData() {
		const drawData: ILegendDrawData[] = [];
		if (this.config.values) {
			const text = locator(this.config.values.text);
			const fill = locator(this.config.values.color);
			this._data.map((item, index) => {
				drawData.push({
					id: item.id,
					text: text(item).toString(),
					alpha: 1,
					fill: fill(item).toString() || getDefaultColor(index),
					active: !item.$hidden,
				});
			});
		} else if (this.config.treeSeries) {
			const series = this.config.treeSeries;
			if (this.config.type === "groupName") {
				series.forEach(serie => {
					drawData.push({
						id: serie.id,
						text: serie.name || serie.id,
						alpha: 1,
						fill: serie.color,
						active: serie.active,
					});
				});
			} else {
				const getText = serie => {
					if (serie.from && serie.to) return `${serie.from} - ${serie.to}`;
					if (serie.less) return `<= ${serie.less}`;
					if (serie.greater) return `>= ${serie.greater}`;
				};
				series.forEach(serie => {
					drawData.push({
						id: serie.id,
						text: getText(serie),
						alpha: 1,
						fill: serie.color,
						active: serie.active,
					});
				});
			}
		} else {
			const series = this.config.$seriesInfo;
			for (const serie of series) {
				const seriaConfig = serie.config;

				const useColor = seriaConfig.fill && seriaConfig.color;
				drawData.push({
					id: seriaConfig.id,
					text: seriaConfig.name || seriaConfig.value,
					alpha: seriaConfig.alpha,
					fill: seriaConfig.fill || seriaConfig.color,
					color: useColor && seriaConfig.color,
					active: seriaConfig.active,
				});
			}
		}
		return drawData;
	}
}
